import { TFunction } from 'i18next';

/**
 * State-affiliation categories.
 *
 * The UI works with the NEW categories only. The backend still stores and
 * accepts the LEGACY enum (see POST /API/v1/source_management/affiliations),
 * so every value that goes to the API is expanded with `toApiAffiliationTypes`
 * and every value that comes back is folded with `normalizeAffiliationType`.
 *
 * Once the backend migration lands, make `NEW_TO_LEGACY` an identity map and
 * drop `LEGACY_TO_NEW`; nothing else should need to change.
 */
export const AFFILIATION_TYPE_VALUES = [
  'OFFICIAL_STATE_CHANNELS',
  'STATE_CONTROLLED_OUTLETS',
  'STATE_LINKED_CHANNELS',
  'STATE_ALIGNED_CHANNELS',
] as const;

export type AffiliationType = (typeof AFFILIATION_TYPE_VALUES)[number];

/** Legacy backend enum → new category. */
const LEGACY_TO_NEW: Record<string, AffiliationType> = {
  STATE_AGENCY: 'OFFICIAL_STATE_CHANNELS',
  GOVERNMENT_OFFICIAL: 'OFFICIAL_STATE_CHANNELS',
  STATE_OWNED_MEDIA: 'STATE_CONTROLLED_OUTLETS',
  REPORTEDLY_STATE_AFFILIATED: 'STATE_LINKED_CHANNELS',
  STATE_ALIGNED_CHANNELS: 'STATE_ALIGNED_CHANNELS',
};

/** New category → legacy backend enum values it covers (first one is canonical). */
const NEW_TO_LEGACY: Record<AffiliationType, string[]> = {
  OFFICIAL_STATE_CHANNELS: ['STATE_AGENCY', 'GOVERNMENT_OFFICIAL'],
  STATE_CONTROLLED_OUTLETS: ['STATE_OWNED_MEDIA'],
  STATE_LINKED_CHANNELS: ['REPORTEDLY_STATE_AFFILIATED'],
  STATE_ALIGNED_CHANNELS: ['STATE_ALIGNED_CHANNELS'],
};

const isAffiliationType = (value: unknown): value is AffiliationType =>
  typeof value === 'string' && (AFFILIATION_TYPE_VALUES as readonly string[]).includes(value);

const unique = <T,>(values: T[]): T[] => Array.from(new Set(values));

/** Fold a legacy or new value into a new category; `undefined` for unknown input. */
export const normalizeAffiliationType = (
  value: string | null | undefined,
): AffiliationType | undefined => {
  if (!value) return undefined;
  if (isAffiliationType(value)) return value;
  return LEGACY_TO_NEW[value];
};

/** Fold a list of legacy/new values into unique new categories, dropping unknowns. */
export const normalizeAffiliationTypes = (
  values: ReadonlyArray<string | null | undefined> | null | undefined,
): AffiliationType[] =>
  unique(
    (values ?? [])
      .map(normalizeAffiliationType)
      .filter((v): v is AffiliationType => v !== undefined),
  );

/**
 * Expand new categories into the legacy enum values the API currently accepts.
 * Values that are already legacy (e.g. from an old bookmarked URL) pass through.
 */
export const toApiAffiliationTypes = (
  values: ReadonlyArray<string | number | boolean | null | undefined> | null | undefined,
): string[] =>
  unique(
    (values ?? [])
      .filter((v): v is string => typeof v === 'string' && v.length > 0)
      .flatMap((v) => (isAffiliationType(v) ? NEW_TO_LEGACY[v] : [v])),
  );

/**
 * Pick the single legacy enum value to persist for a source affiliation.
 * Keeps the source's existing legacy value when it already belongs to the
 * selected category, so editing another field doesn't silently reclassify it.
 */
export const toApiAffiliationEntityType = (
  selected: string | null | undefined,
  existing?: string | null,
): string | null => {
  const category = normalizeAffiliationType(selected);
  if (!category) return selected ?? null;
  if (existing && normalizeAffiliationType(existing) === category) return existing;
  return NEW_TO_LEGACY[category][0];
};

/** Untranslated labels, keyed by new category (pass through `t`). */
export const AFFILIATION_TYPE_LABELS: Record<AffiliationType, string> = {
  OFFICIAL_STATE_CHANNELS: 'Official state channels',
  STATE_CONTROLLED_OUTLETS: 'State-controlled outlets',
  STATE_LINKED_CHANNELS: 'State-linked channels',
  STATE_ALIGNED_CHANNELS: 'State-aligned channels',
};

/** Translated label for a legacy or new value; `undefined` for unknown input. */
export const getAffiliationTypeLabel = (
  t: TFunction,
  value: string | null | undefined,
): string | undefined => {
  const category = normalizeAffiliationType(value);
  return category ? t(AFFILIATION_TYPE_LABELS[category]) : undefined;
};

/**
 * Translated labels keyed by BOTH new and legacy values, so lookups on raw
 * backend data keep working during the migration.
 */
export const mapAffiliationTypeLabels = (t: TFunction): Record<string, string> => {
  const labels: Record<string, string> = {};
  AFFILIATION_TYPE_VALUES.forEach((value) => {
    labels[value] = t(AFFILIATION_TYPE_LABELS[value]);
  });
  Object.entries(LEGACY_TO_NEW).forEach(([legacy, value]) => {
    labels[legacy] = t(AFFILIATION_TYPE_LABELS[value]);
  });
  return labels;
};

export interface AffiliationTypeOption {
  value: AffiliationType;
  label: string;
}

/** Select/checkbox options: new categories only. */
export const getAffiliationTypeOptions = (t: TFunction): AffiliationTypeOption[] =>
  AFFILIATION_TYPE_VALUES.map((value) => ({ value, label: t(AFFILIATION_TYPE_LABELS[value]) }));

/** Resolve legacy or new values to options (deduplicated, unknowns dropped). */
export const getAffiliationTypeOptionsByValues = (
  t: TFunction,
  values: ReadonlyArray<string | null | undefined> | null | undefined,
): AffiliationTypeOption[] => {
  const options = getAffiliationTypeOptions(t);
  return normalizeAffiliationTypes(values)
    .map((value) => options.find((option) => option.value === value))
    .filter((option): option is AffiliationTypeOption => option !== undefined);
};
